// ============================================================
// FILE: hooks/usePermissions.js — supports ALL roles dynamically
// ============================================================
import { useState, useEffect, useCallback } from 'react';
import config from '../config';

const BASE = (config.API_BASE_URL || config || '').replace(/\/$/, '');

export const usePermissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [roleName, setRoleName]       = useState('');
  const [roleId, setRoleId]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const userRole  = (localStorage.getItem('userRole') || '').toLowerCase().trim();
      const adminData = JSON.parse(localStorage.getItem('admin') || '{}');
      const userData  = JSON.parse(localStorage.getItem('user')  || '{}');
      const dbRole    = (adminData.role || '').toLowerCase().replace(/\s+/g, '_').trim();

      // ── 1. HARDCODED SUPERADMIN ───────────────────────────
      const isSA =
        userRole === 'superadmin'  ||
        userRole === 'super_admin' ||
        dbRole   === 'superadmin'  ||
        dbRole   === 'super_admin';

      if (isSA) {
        setRoleName(adminData.role || 'Super Admin');
        setRoleId(null);
        setPermissions([]);
        setLoading(false);
        return;
      }

      // ── 2. REGULAR USER ───────────────────────────────────
      if (userRole === 'user') {
        const userId = userData.id || userData.userId;

        if (!userId) {
          setRoleName('User');
          setPermissions([]);
          setLoading(false);
          return;
        }

        try {
          const res = await fetch(`${BASE}/Permissions/user/${userId}`);
          if (res.ok) {
            const data = await res.json();
            setPermissions(data.permissions || []);
            setRoleName(data.roleName || 'User');
            setRoleId(data.roleId || null);
          } else {
            setRoleName('User');
            setPermissions([]);
          }
        } catch {
          setRoleName('User');
          setPermissions([]);
        }

        setLoading(false);
        return;
      }

      // ── 3. ALL OTHER ROLES (Admin, Marketing, Staff, Developer,
      //        IT, Tech, Technical, HR, Finance, Manager, etc.)
      //        — fetch permissions dynamically from DB ──────────
      const dbRoleName = adminData.role || ''; // e.g. "Marketing", "Admin", "Staff"

      try {
        // Step 1: Get all roles to find matching role_id by name
        const rolesRes = await fetch(`${BASE}/Permissions/roles`);
        if (!rolesRes.ok) throw new Error('Failed to fetch roles');
        const rolesData = await rolesRes.json();

        console.log('[usePermissions] All roles:', rolesData);
        console.log('[usePermissions] Looking for DB role:', dbRoleName);

        // Find matching role — case-insensitive
        const matchedRole = rolesData.find(r =>
          r.roleName?.toLowerCase().trim() === dbRoleName.toLowerCase().trim()
        );

        console.log('[usePermissions] Matched role:', matchedRole);

        if (!matchedRole) {
          console.warn('[usePermissions] No matching role found for:', dbRoleName, '— showing empty sidebar');
          setRoleName(dbRoleName || userRole || 'Staff');
          setPermissions([]);
          setLoading(false);
          return;
        }

        // Step 2: Get permissions for this role_id
        const permRes = await fetch(`${BASE}/Permissions/roles/${matchedRole.id}`);
        if (!permRes.ok) throw new Error('Failed to fetch role permissions');
        const permData = await permRes.json();

        console.log('[usePermissions] Permission data:', permData);

        setPermissions(permData.permissions || []);
        setRoleName(matchedRole.roleName || dbRoleName);
        setRoleId(matchedRole.id);

      } catch (err) {
        console.error('[usePermissions] Error fetching role permissions:', err);
        setRoleName(dbRoleName || userRole || 'Staff');
        setPermissions([]);
      }

      setLoading(false);

    } catch (err) {
      console.error('[usePermissions] Unexpected error:', err);
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // ── Helper checks ─────────────────────────────────────────
  const userRole  = (localStorage.getItem('userRole') || '').toLowerCase().trim();
  const adminData = JSON.parse(localStorage.getItem('admin') || '{}');
  const dbRole    = (adminData.role || '').toLowerCase().replace(/\s+/g, '_').trim();

  const isSuperAdmin =
    userRole === 'superadmin'  ||
    userRole === 'super_admin' ||
    dbRole   === 'superadmin'  ||
    dbRole   === 'super_admin';

  const canView = useCallback((key) => {
    if (isSuperAdmin) return true;
    return permissions.find(p => p.permissionKey === key)?.canView ?? false;
  }, [permissions, isSuperAdmin]);

  const canCreate = useCallback((key) => {
    if (isSuperAdmin) return true;
    return permissions.find(p => p.permissionKey === key)?.canCreate ?? false;
  }, [permissions, isSuperAdmin]);

  const canEdit = useCallback((key) => {
    if (isSuperAdmin) return true;
    return permissions.find(p => p.permissionKey === key)?.canEdit ?? false;
  }, [permissions, isSuperAdmin]);

  const canDelete = useCallback((key) => {
    if (isSuperAdmin) return true;
    return permissions.find(p => p.permissionKey === key)?.canDelete ?? false;
  }, [permissions, isSuperAdmin]);

  return {
    permissions,
    roleName,
    roleId,
    isSuperAdmin,
    loading,
    error,
    canView,
    canCreate,
    canEdit,
    canDelete,
    refetch: fetchPermissions,
  };
};